import type { FileDeleteMessageDatagramBuf } from "~/classes/generated/message"
import type { FileDeleteResponseDatagramBuf } from "~/classes/generated/response"

import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { FileDeleteRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/delete")

export function sendFileDeleteRequests(requests: FileDeleteRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()
  const fbb = new DatagramBufferBuilder(64)
  const requestVector = []
  for (const request of requests) {
    const file = addFileToBuf(fbb, request.file)
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    FileDeleteRequestDatagramBuf.startFileDeleteRequestDatagramBuf(fbb)
    FileDeleteRequestDatagramBuf.addRequestId(fbb, requestId)
    FileDeleteRequestDatagramBuf.addFile(fbb, file)
    FileDeleteRequestDatagramBuf.addOffline(fbb, request.offline)
    const deleteRequest = FileDeleteRequestDatagramBuf.endFileDeleteRequestDatagramBuf(fbb)
    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addFileDeleteRequest(fbb, deleteRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const vector = DatagramBuf.createRequestVector(fbb, requestVector)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, vector)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleFileDeleteResponseDatagramBuf(datagram: FileDeleteResponseDatagramBuf) {
  // TODO: get rid of datagram result, and find out a better way to
  // error out of this, since we need to send notifications
  onFileDeleteResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    fileId: parseFileId(datagram.fileId()!),
    file: maybe(parseFile)(datagram.file()),
    offline: datagram.offline(),
  })
}

export function onFileDeleteResponse(response: FileDeleteResponseDatagram) {
  const { emitEntityEvent } = useEvents()

  if (response.error)
    return onFileDeleteErrored(response.error)
  if (!response.file)
    throw new Error("No file in delete response")

  const iFile = new InterfaceFile(response.file)

  emitEntityEvent("fileDeleted", iFile.fileId, iFile.inner(), response.offline)

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file delete response", response)
    throw new Error("no parent id in file delete response")
  }
  emitEntityEvent("childDeleted", parentId, iFile.inner(), response.offline)
}

export function onFileDeleteErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "File deletion was unsuccessful")
}

export function handleFileDeleteMessageDatagramBuf(datagram: FileDeleteMessageDatagramBuf) {
  onFileDeleteMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    file: parseFile(datagram.file()!),
  })
}

export function onFileDeleteMessage(datagram: FileDeleteMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  const iFile = new InterfaceFile(datagram.file)

  emitEntityEvent("fileDeleted", iFile.fileId, iFile.inner(), false)

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file delete response", datagram)
    throw new Error("no parent id in file delete response")
  }
  emitEntityEvent("childDeleted", parentId, iFile.inner(), false)
}
